import React, { useState, useRef, useEffect } from "react";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencyCode } from "@/models/currency";
import styles from "@/styles/common/CurrencySelector.module.scss";
import { useOnClickOutside } from "@/hooks/useOnClickOutiside";
// Import flag images from country-flag-icons
import EU from "country-flag-icons/react/3x2/EU";
import GB from "country-flag-icons/react/3x2/GB";
import US from "country-flag-icons/react/3x2/US";
import CA from "country-flag-icons/react/3x2/CA";
import AU from "country-flag-icons/react/3x2/AU";

// Mappa delle bandiere per valuta
const FLAG_COMPONENTS: Record<
    CurrencyCode,
    React.ComponentType<{ className?: string; title?: string }>
> = {
    EUR: EU,
    GBP: GB,
    USD: US,
    CAD: CA,
    AUD: AU,
};

/**
 * Componente per selezionare la valuta preferita dall'utente
 * - Dropdown con lista valute disponibili
 * - Mostra bandiera, codice e simbolo della valuta corrente
 * - Persiste la scelta in localStorage
 * - Invalida le query per ricaricare i dati con la nuova valuta
 */
const CurrencySelector: React.FC = () => {
    const {
        currency,
        updateCurrency,
        currencies,
        currentCurrencyInfo,
        isLoading,
    } = useCurrency();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Chiudi dropdown quando si clicca fuori
    useOnClickOutside([dropdownRef], () => {
        if (isOpen) setIsOpen(false);
    });

    // Chiudi dropdown quando si preme ESC
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) {
                setIsOpen(false);
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen]);

    const handleSelect = (code: CurrencyCode) => {
        if (code === currency) {
            setIsOpen(false);
            return;
        }

        updateCurrency(code);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    if (isLoading) {
        return (
            <div className={styles.currencySelector}>
                <button
                    type="button"
                    className={styles.currencyButton}
                    disabled
                >
                    <span className={styles.currencyFlag}>🌍</span>
                    <span className={styles.currencyCode}>...</span>
                </button>
            </div>
        );
    }

    return (
        <div className={styles.currencySelector} ref={dropdownRef}>
            {/* Bottone principale */}
            <button
                className={`${styles.currencyButton} ${
                    isOpen ? styles.active : ""
                }`}
                onClick={toggleDropdown}
                aria-label="Select currency"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                type="button"
            >
                {currentCurrencyInfo &&
                    FLAG_COMPONENTS[currentCurrencyInfo.code] && (
                        <span
                            className={styles.currencyFlag}
                            role="img"
                            aria-label={currentCurrencyInfo.name}
                        >
                            {React.createElement(
                                FLAG_COMPONENTS[currentCurrencyInfo.code],
                                {
                                    className: styles.flagIcon,
                                    title: currentCurrencyInfo.name,
                                }
                            )}
                        </span>
                    )}
                <span className={styles.currencyCode}>
                    {currentCurrencyInfo?.code}
                </span>
                <svg
                    className={`${styles.chevron} ${
                        isOpen ? styles.chevronUp : ""
                    }`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M2 4L6 8L10 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className={styles.currencyDropdown} role="listbox">
                    {currencies.map(curr => (
                        <button
                            key={curr.code}
                            className={`${styles.currencyOption} ${
                                curr.code === currency
                                    ? styles.optionActive
                                    : ""
                            }`}
                            onClick={() => handleSelect(curr.code)}
                            role="option"
                            aria-selected={curr.code === currency}
                            type="button"
                        >
                            {/* Bandiera invece dell'acronimo */}
                            {FLAG_COMPONENTS[curr.code] && (
                                <span
                                    className={styles.optionFlag}
                                    role="img"
                                    aria-label={curr.name}
                                >
                                    {React.createElement(
                                        FLAG_COMPONENTS[curr.code],
                                        {
                                            className: styles.flagIcon,
                                            title: curr.name,
                                        }
                                    )}
                                </span>
                            )}
                            <div className={styles.optionDetails}>
                                <span className={styles.optionCode}>
                                    {curr.code}
                                </span>
                                <span className={styles.optionName}>
                                    {curr.name}
                                </span>
                            </div>
                            <span className={styles.optionSymbol}>
                                {curr.symbol}
                            </span>
                            {curr.code === currency && (
                                <svg
                                    className={styles.checkIcon}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.3334 4L6.00002 11.3333L2.66669 8"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CurrencySelector;
